import { useRef, useState } from "react";

import { useFeatureflags } from "@web-app/api";
import { FieldValue, Order } from "@web-app/types";
import { SCANNABLE_CODE_FEATURE } from "@web-app/utils/scannableCode";

import { ScannableCodeEntriesEditor } from "../../../../ScannableCodeEntries/ScannableCodeEntriesEditor";
import {
  ScannableCodeEntry,
  toOrderCodes,
  toScannableCodeEntries,
  validateScannableCodeEntries,
} from "../../../../ScannableCodeEntries/scannableCodeEntries";

// Matches the wait of debouncedHandleFieldChange in OrderEditPanel.
const DEBOUNCE_MS = 1000;

// Typing in a code or quantity changes only those fields of existing rows.
const isTypingChange = (
  prev: ScannableCodeEntry[],
  next: ScannableCodeEntry[],
) =>
  prev.length === next.length &&
  next.every(
    (entry, i) =>
      entry.format === prev[i].format &&
      entry.isRequired === prev[i].isRequired,
  );

/**
 * Edits the codes to scan at one stop of an order, saving as rows change. Like
 * the rest of the edit panel, typing (code, quantity) is debounced and other
 * changes (format, required, removing a row) save at once. Rows without a code
 * are drafts and are left out of the save; other invalid rows block it. Render
 * with key={order.id} so switching orders resets the rows. Hidden unless the
 * scannableCode flag is on.
 */
export default function ScannableCodeDetailsEditField({
  debouncedHandleFieldChange,
  onFieldChange,
  order,
  stop,
}: {
  debouncedHandleFieldChange: (property: string, value: FieldValue) => void;
  onFieldChange: (property: string, value: FieldValue) => unknown;
  order: Order;
  stop: "dropoff" | "pickup";
}) {
  const { hasFeature } = useFeatureflags();
  const property = `${stop}.codes`;
  const [entries, setEntries] = useState(() =>
    toScannableCodeEntries(order[stop]?.codes),
  );
  const [error, setError] = useState<string>();
  const lastSaved = useRef(JSON.stringify(toOrderCodes(entries)));
  const debouncedUntil = useRef(0);

  const handleChange = (next: ScannableCodeEntry[]) => {
    const isTyping = isTypingChange(entries, next);
    setEntries(next);

    const toSave = next.filter((entry) => entry.code.trim());
    const result = validateScannableCodeEntries(toSave);
    if (result !== true) {
      setError(result);
      return;
    }
    setError(undefined);

    // The API replaces the stop's whole list; scans are stored separately.
    const codes = toOrderCodes(toSave);
    const serialized = JSON.stringify(codes);
    // e.g. adding an empty row: nothing to save yet.
    if (serialized === lastSaved.current) return;
    lastSaved.current = serialized;

    // While a debounced save may still be queued, an immediate save would land
    // first and then be overwritten by the older queued value, so replace the
    // queued one instead.
    if (isTyping || Date.now() < debouncedUntil.current) {
      debouncedUntil.current = Date.now() + DEBOUNCE_MS;
      debouncedHandleFieldChange(property, codes);
    } else {
      onFieldChange(property, codes);
    }
  };

  if (!hasFeature(SCANNABLE_CODE_FEATURE)) return null;

  return (
    <ScannableCodeEntriesEditor
      entries={entries}
      error={error}
      onChange={handleChange}
    />
  );
}
